import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Button, Switch, Select, Alert } from "antd";
import { PatientResourceLink } from "../../../../../types/file/ResourceLinkTypes";
import { 
	createPatientResourceLink, 
	updatePatientResourceLink,
	getGlobalResourceLinks,
	assignGlobalResourceLinksToPatient 
} from "../../../../../api/file/patientResourceLinksApi";
import { useNotifications } from "../../../../../context/NotificationContext";
import { extractValidationErrors } from "../../../../../utils/apiError";
import type { UserUuid } from "../../../../../types/identity";

const { TextArea } = Input;
const { Option } = Select;

interface ResourceLinkModalProps {
	visible: boolean;
	onClose: () => void;
	onResourceLinkSaved: () => void;
	patientId: UserUuid;
	companyId: number;
	editingLink?: PatientResourceLink | null;
}

const ResourceLinkModal: React.FC<ResourceLinkModalProps> = ({
	visible,
	onClose,
	onResourceLinkSaved,
	patientId,
	companyId,
	editingLink,
}) => {
	const { showSuccess, showError } = useNotifications();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [globalResources, setGlobalResources] = useState<any[]>([]);
	const [useGlobalResource, setUseGlobalResource] = useState(true);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);

	useEffect(() => {
		if (visible) {
			setErrorMessages([]);
			if (editingLink) {
				form.setFieldsValue({
					resource_type: editingLink.resource_type,
					link_url: editingLink.link_url,
					description: editingLink.description,
					is_active: editingLink.is_active,
				});
			} else {
				form.resetFields();
				// Fetch global resources when creating new link
				fetchGlobalResources();
			}
		}
	}, [visible, editingLink, form]);

	const fetchGlobalResources = async () => {
		try {
			const response = await getGlobalResourceLinks();
			const sorted = (response || []).slice().sort((a: any, b: any) =>
				a.resource_type.localeCompare(b.resource_type)
			);
			setGlobalResources(sorted);
		} catch (error) {
			console.error("Error fetching global resources:", error);
			showError("Failed to fetch global resources");
		}
	};

	const handleSubmit = async (values: any) => {
		setLoading(true);
		setErrorMessages([]);
		try {
			if (useGlobalResource && values.globalResourceIds?.length) {
				// Assign each selected global resource to patient
				await Promise.all(
					values.globalResourceIds.map((id: number) =>
						assignGlobalResourceLinksToPatient(companyId, patientId, id)
					)
				);
				showSuccess(`${values.globalResourceIds.length} global resource(s) assigned successfully!`);
			} else {
				// Create or update custom resource link
				const linkData: PatientResourceLink = {
					id: editingLink ? editingLink.id : 0,
					company_id: companyId,
					patient_id: patientId,
					resource_type: values.resource_type,
					link_url: values.link_url,
					description: values.description,
					is_active: values.is_active ?? true,
					is_global: false,
					created_at: editingLink ? editingLink.created_at : new Date(),
					updated_at: editingLink ? editingLink.updated_at : new Date(),
					admin_template_id: null
				};

				if (editingLink) {
					await updatePatientResourceLink(linkData);
					showSuccess("Resource link updated successfully!");
				} else {
					await createPatientResourceLink(linkData);
					showSuccess("Resource link created successfully!");
				}
			}

			onResourceLinkSaved();
			onClose();
		} catch (error) {
			console.error("Error saving resource link:", error);
			// The file service returns request-validation failures as 422 with a per-field error map;
			// surface those messages instead of a generic, non-actionable failure.
			const messages = extractValidationErrors(error);
			setErrorMessages(messages);
			showError(messages[0] ?? "Failed to save resource link");
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		setErrorMessages([]);
		setUseGlobalResource(false);
		onClose();
	};

	return (
		<Modal
			title={editingLink ? "Edit Resource Link" : "Add Resource Link"}
			open={visible}
			onCancel={handleCancel}
			destroyOnClose
			footer={null}
			width={600}
		>
			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
			>
				{errorMessages.length > 0 && (
					<Alert
						type="error"
						showIcon
						closable
						className="mb-4"
						data-testid="files-resource-link-error"
						message={errorMessages.length === 1 ? errorMessages[0] : "Please fix the following:"}
						description={
							errorMessages.length > 1 ? (
								<ul className="list-disc pl-4">
									{errorMessages.map((msg, i) => (
										<li key={i}>{msg}</li>
									))}
								</ul>
							) : undefined
						}
						onClose={() => setErrorMessages([])}
					/>
				)}

				{!editingLink && (
					<Form.Item label="Resource Source">
						<Switch
							checked={useGlobalResource}
							onChange={setUseGlobalResource}
							checkedChildren="Global Resource"
							unCheckedChildren="Custom Link"
						/>
					</Form.Item>
				)}

				{useGlobalResource && !editingLink ? (
					<Form.Item
						name="globalResourceIds"
						label="Select Global Resources"
						rules={[{ required: true, message: "Please select at least one global resource" }]}
					>
						<Select
							mode="multiple"
							placeholder="Choose one or more global resources"
							allowClear
							optionFilterProp="children"
						>
							{globalResources.map((resource) => (
								<Option key={resource.id} value={resource.id}>
									{resource.resource_type} - {resource.description}
								</Option>
							))}
						</Select>
					</Form.Item>
				) : (
					<>
						<Form.Item
							name="resource_type"
							label="Resource Name"
							rules={[{ required: true, message: "Please enter a name for the resource" }]}
						>
							<Input placeholder="e.g., Educational Material, Therapath Website, etc." />
						</Form.Item>

						<Form.Item
							name="link_url"
							label="URL"
							rules={[
								{ required: true, message: "Please enter URL" },
								{ type: "url", message: "Please enter a valid URL" }
							]}
						>
							<Input placeholder="https://example.com" />
						</Form.Item>

						<Form.Item
							name="description"
							label="Description"
							rules={[{ required: true, message: "Please enter description" }]}
						>
							<TextArea 
								rows={3} 
								placeholder="Brief description of the resource" 
							/>
						</Form.Item>

						<Form.Item
							name="is_active"
							label="Active"
							valuePropName="checked"
							initialValue={true}
						>
							<Switch />
						</Form.Item>
					</>
				)}

				<Form.Item>
					<div className="flex justify-end space-x-2">
						<Button onClick={handleCancel}>
							Cancel
						</Button>
						<Button type="primary" htmlType="submit" loading={loading}>
							{editingLink ? "Update" : "Save"}
						</Button>
					</div>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default ResourceLinkModal;